import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import { KTSVG as KtSvg } from '../../../../_metronic/helpers';
import { type ApiResponse, type ApiResponseError } from '../../../../api/core/_models';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { TaskStatusType } from '@/app/enums/TaskStatusType';
import { type TaskInfoDto } from '@/app/models/TaskInfoDto';
import { getTaskStatus } from '../core/_requests';

export type TaskProgressButtonProps = {
  label: string;
  runningLabel?: string;
  iconPath: string;
  buttonClassName: string;
  startTask: () => Promise<ApiResponse<TaskInfoDto<string>>>;
  successMessage: string;
  errorMessage: string;
  progressBarColorClass?: string;
  disabled?: boolean;
  onSuccess?: (task: TaskInfoDto<string>) => void;
  onFailure?: (task: TaskInfoDto<string>) => void;
  taskInfo?: TaskInfoDto<string>;
};

function TaskProgressButton({
  label,
  runningLabel = 'Gerando...',
  iconPath,
  buttonClassName,
  startTask,
  successMessage,
  errorMessage,
  progressBarColorClass = 'bg-primary',
  disabled = false,
  onSuccess,
  onFailure,
  ...props
}: TaskProgressButtonProps) {
  const [taskInfo, setTaskInfo] = useState<TaskInfoDto<string> | null>(null);
  const [isWatchingTask, setIsWatchingTask] = useState<boolean>(false);

  const startTaskMutation = useMutation({
    mutationFn: startTask,
    onSuccess: (response) => {
      const taskData = response.data;
      setTaskInfo(taskData ?? null);
    },
    onError: (error) => {
      SystemNotification.error(getErrorMessage(error));
    },
  });

  useEffect(() => {
    if (props.taskInfo) {
      setTaskInfo(props.taskInfo);
    }
  }, [props.taskInfo]);

  useEffect(() => {
    if (!taskInfo?.name) {
      return undefined;
    }

    if (taskInfo.status !== TaskStatusType.RUNNING) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      getTaskStatus(taskInfo.name)
        .then((updated) => setTaskInfo(updated.data ?? null))
        .catch((error: ApiResponseError) => {
          SystemNotification.error(getErrorMessage(error));
          window.clearInterval(intervalId);
        });
    }, 1000);

    setIsWatchingTask(true);

    return () => window.clearInterval(intervalId);
  }, [taskInfo?.name, taskInfo?.status]);

  useEffect(() => {
    if (!taskInfo?.status || !isWatchingTask) {
      return;
    }

    if (taskInfo.status === TaskStatusType.COMPLETED) {
      SystemNotification.success(successMessage);
      onSuccess?.(taskInfo);
      setTaskInfo(null);
      return;
    }

    // FAILED ou CANCELLED: todo fim que não seja COMPLETED é falha, como no módulo de export.
    if (taskInfo.status !== TaskStatusType.RUNNING) {
      SystemNotification.error(taskInfo.error || errorMessage);
      onFailure?.(taskInfo);
      setTaskInfo(null);
    }
  }, [errorMessage, onFailure, onSuccess, successMessage, taskInfo, isWatchingTask]);

  const isTaskRunning = taskInfo?.status === TaskStatusType.RUNNING;
  const progressPercent = useMemo(() => {
    const rawProgress = taskInfo?.progress ?? 0;
    return Math.max(0, Math.min(100, rawProgress > 1 ? rawProgress : rawProgress * 100));
  }, [taskInfo?.progress]);

  const showProgress = isTaskRunning || startTaskMutation.isPending;
  const isDisabled = disabled || startTaskMutation.isPending || isTaskRunning;

  return (
    <button
      className={buttonClassName}
      disabled={isDisabled}
      title={label}
      type="button"
      onClick={() => startTaskMutation.mutate()}
    >
      {showProgress ? (
        <div className="d-flex flex-column align-items-center w-100">
          <span className="fw-bold text-gray-800 mb-2">{runningLabel}</span>
          <div className="progress w-100 h-6px">
            <div
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={progressPercent}
              className={`progress-bar ${progressBarColorClass}`}
              role="progressbar"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      ) : (
        <>
          <KtSvg className="svg-icon-2x mb-2" path={iconPath} />
          <span className="fw-bold text-gray-800">{label}</span>
        </>
      )}
    </button>
  );
}

export default TaskProgressButton;
